import { useEffect, useState } from "react";
import { Globe, MapPin, CaretLeft, ChatCircleText, CalendarBlank, Crown, House } from "@phosphor-icons/react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { coverImage, onPropImgError } from "../lib/helpers";
import LeadLanderPanel from "./LeadLanderPanel";
import { Badge } from "./ui/badge";

function ManageView({ propertyId, onBack, isAdmin, penthouse }) {
  const { user } = useAuth();
  const [p, setP] = useState(null);
  useEffect(() => {
    client.get(`/properties/${propertyId}`).then((r) => setP(r.data)).catch(() => setP(null));
  }, [propertyId]);
  const patch = async (body) => {
    const r = await client.patch(`/properties/${propertyId}`, body);
    setP(r.data);
  };
  if (!p) return <p className="text-sm text-[#5C5A58]" data-testid="ll-hub-loading">Loading…</p>;
  return (
    <div className="max-w-3xl space-y-5" data-testid="ll-hub-manage">
      <button onClick={onBack} data-testid="ll-hub-back" className="flex items-center gap-1.5 text-sm text-[#5C5A58] hover:text-[#4A0000] transition-colors">
        <CaretLeft size={16} /> All LeadLander pages
      </button>
      <div>
        <h2 className="font-display text-3xl text-[#4A0000] truncate">{p.title}</h2>
        <p className="text-sm text-[#5C5A58] flex items-center gap-1 mt-1"><MapPin size={14} weight="duotone" />{p.address}</p>
      </div>
      <LeadLanderPanel p={p} patch={patch} isAdmin={isAdmin}
        isOwnerAgent={user.role === "agent" && p.agent_id === user.id}
        penthouse={penthouse} />
    </div>
  );
}

export default function LeadLanderHub({ initialPropertyId, onConsumed, onGoPenthouse }) {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const penthouse = !!user.penthouse_partner;
  const [items, setItems] = useState([]);
  const [partner, setPartner] = useState(true);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(initialPropertyId || null);

  const load = () => client.get("/leadlander/overview")
    .then((r) => { setItems(r.data.items); setPartner(r.data.partner); })
    .catch(() => {}).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (initialPropertyId) { setSelected(initialPropertyId); onConsumed && onConsumed(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPropertyId]);

  if (selected) {
    return <ManageView propertyId={selected} isAdmin={isAdmin} penthouse={penthouse}
      onBack={() => { setSelected(null); load(); }} />;
  }

  return (
    <div className="max-w-4xl space-y-6" data-testid="leadlander-hub">
      <div>
        <p className="eyebrow text-xs text-[#B08D57] mb-1">Marketing</p>
        <h2 className="font-display text-3xl text-[#4A0000]">LeadLander</h2>
        <p className="text-sm text-[#5C5A58] mt-1">Every shoot gets a shareable public property page. Manage its content, open houses and the leads that come in — a Penthouse Partner perk.</p>
      </div>

      {loading ? (
        <p className="text-sm text-[#5C5A58]">Loading your pages…</p>
      ) : !partner ? (
        <div className="text-center py-16 border border-dashed border-[#E4D08A] rounded-md bg-[#B08D57]/[0.06]" data-testid="ll-hub-upsell">
          <Crown size={40} weight="fill" className="mx-auto mb-3 text-[#B08D57]" />
          <p className="text-[#8A6D1B] font-medium">Become a Penthouse Partner to unlock LeadLander leads, open houses and tour requests.</p>
          {onGoPenthouse && (
            <button onClick={onGoPenthouse} data-testid="ll-hub-upsell-cta"
              className="mt-4 inline-flex items-center gap-2 rounded-md px-5 py-2.5 bg-[#B08D57] hover:bg-[#C59B47] text-[#1A1715] text-sm font-bold transition-colors shadow-[0_8px_22px_-8px_rgba(176,141,87,0.8)]">
              <Crown size={16} weight="fill" />Become a Penthouse Partner
            </button>
          )}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-[#5C5A58] border border-dashed border-[#E9DDC6] rounded-md" data-testid="ll-hub-empty">
          <House size={40} weight="duotone" className="mx-auto mb-3 text-[#4A0000]" />
          <p>No shoots yet. Book a shoot and its LeadLander page appears here automatically.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="ll-hub-grid">
          {items.map((it) => (
            <button key={it.id} onClick={() => setSelected(it.id)} data-testid={`ll-hub-card-${it.id}`}
              className="text-left bg-white border border-[#E9DDC6] rounded-[5px] overflow-hidden hover:-translate-y-[2px] hover:shadow-premium-lg hover:border-[#D9CFB0] transition-[transform,box-shadow,border-color] duration-200 group">
              <div className="h-32 bg-[#FAF9F6] overflow-hidden relative">
                <img src={coverImage(it)} alt={it.title} onError={onPropImgError} className="w-full h-full object-cover transition-transform duration-[600ms] group-hover:scale-[1.05]" />
                {it.new_leads > 0 && (
                  <span data-testid={`ll-hub-new-${it.id}`} className="absolute top-2 right-2 rounded-full bg-[#B08D57] text-[#1A1715] text-[11px] font-bold px-2 py-0.5 shadow">
                    {it.new_leads} new
                  </span>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-display text-lg leading-tight line-clamp-1 group-hover:text-[#4A0000] transition-colors">{it.title}</h3>
                <p className="text-xs text-[#5C5A58] flex items-center gap-1 mt-0.5 min-w-0"><MapPin size={12} weight="duotone" className="shrink-0" /><span className="truncate">{it.address}</span></p>
                <div className="flex items-center gap-2 mt-3">
                  <Badge className="bg-[#4A0000]/5 text-[#4A0000] border-[#4A0000]/20 flex items-center gap-1 text-[11px]"><ChatCircleText size={11} weight="fill" />{it.total_leads} lead{it.total_leads === 1 ? "" : "s"}</Badge>
                  <Badge className="bg-[#FAF9F6] text-[#5C5A58] border-[#E9DDC6] flex items-center gap-1 text-[11px]"><CalendarBlank size={11} weight="fill" />{it.open_house_count} open house{it.open_house_count === 1 ? "" : "s"}</Badge>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
